import { useState, useCallback, useEffect, useContext } from 'react';
import useConfirmationDialog from './useConfirmationDialog';
import AuthContext from '../contexts/AuthContext';
import LoaderContext from '../contexts/LoaderContext';
import StateProviderContext from '../contexts/StateProviderContext';

export const useHttpClient = () => {
  const auth = useContext(AuthContext);
  const { showLoader } = useContext(LoaderContext);
  const { activeHttpRequests } = useContext(StateProviderContext);
  const { resetDialog } = useConfirmationDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  const handleUserLogout = useCallback(
    async ({ headerValues, httpAbortCtrl }) => {
      const usersData = await fetch(
        `${process.env.REACT_APP_API_URL}users/logout`,
        {
          method: 'POST',
          body: null,
          headers: headerValues,
          signal: httpAbortCtrl.signal,
          credentials: 'include',
        }
      );

      return usersData;
    },
    []
  );

  // const activeHttpRequests = useRef([]);
  // commented on 06/10/2023 due to handling multiple request

  const refreshTokenAndRetry = async ({
    url,
    method,
    body,
    headerValues,
    httpAbortCtrl,
    token,
  }) => {
    const refreshResponse = await fetch(
      `${process.env.REACT_APP_API_URL}users/refresh-token`,
      {
        method: 'POST',
        body: JSON.stringify({
          token,
        }),
        headers: headerValues,
        signal: httpAbortCtrl.signal,
        credentials: 'include',
      }
    );

    if (!refreshResponse.ok) {
      throw new Error('authentication failed.');
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem('token', refreshData.token);

    // Retry the original request with the new token
    const retryHeaders = {
      ...headerValues,
      Authorization: `Bearer ${refreshData.token}`,
    };

    const retryResponse = await fetch(
      `${process.env.REACT_APP_API_URL}${url}`,
      {
        method,
        body,
        headers: retryHeaders,
        signal: httpAbortCtrl.signal,
        credentials: 'include',
      }
    );

    const retryResponseData = await retryResponse.json();
    if (!retryResponse.ok) {
      throw new Error(retryResponseData.message);
    }

    return retryResponseData;
  };

  const sendRequest = useCallback(
    async (
      url,
      method = 'GET',
      body = null,
      headers = {},
      publicRequest,
      isLoaderEnable = true
    ) => {
      setIsLoading(true);
      if (isLoaderEnable) {
        showLoader(true);
      }
      setError('');
      const httpAbortCtrl = new AbortController();
      activeHttpRequests.current.push(httpAbortCtrl);
      const token =
        localStorage.getItem('token') !== undefined
          ? localStorage.getItem('token')
          : '';
      const defaultHeader = {
        Authorization: `Bearer ${token}`,
      };
      const headerValues = publicRequest
        ? { ...headers }
        : { ...headers, ...defaultHeader };

      try {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          throw new Error(
            'Network error. Please check your internet connection.'
          );
        }
        const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
          method,
          body,
          headers: headerValues,
          signal: httpAbortCtrl.signal,
          credentials: 'include',
        });
        const responseData = await response.json();

        // Handle access token expiration (401)
        if (
          response.status === 401 &&
          responseData.message.toLowerCase() === 'authentication failed.' &&
          token
        ) {
          return await refreshTokenAndRetry({
            url,
            method,
            body,
            headerValues,
            httpAbortCtrl,
            token,
          });
        }

        if (!response.ok) {
          throw new Error(responseData.message);
        }
        return responseData;
      } catch (err) {
        if (activeHttpRequests?.current?.length > 0) {
          activeHttpRequests.current = activeHttpRequests.current.filter(
            reqCtrl => reqCtrl !== httpAbortCtrl
          );
        }
        if (err.message.toLowerCase() === 'authentication failed.') {
          await handleUserLogout({
            headerValues,
            httpAbortCtrl,
          });
          auth.logout();
        }
        setError(err.message);
        setIsLoading(false);
        showLoader(false);
        throw err;
      } finally {
        // Remove this abort controller from the array if the request is completed
        if (activeHttpRequests?.current?.length > 0) {
          activeHttpRequests.current = activeHttpRequests.current.filter(
            reqCtrl => reqCtrl !== httpAbortCtrl
          );
        }
        if (
          activeHttpRequests.current &&
          activeHttpRequests.current.length == 0
        ) {
          setIsLoading(false);
          showLoader(false);
        }
        resetDialog();
      }
    },
    [auth, showLoader, activeHttpRequests]
  );

  const clearError = () => {
    setError(null);
  };

  // Clean up function to remove abort controllers
  useEffect(
    () => () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (activeHttpRequests?.current) {
        activeHttpRequests.current.forEach(abortCtrl => abortCtrl.abort());
      }
    },
    [activeHttpRequests]
  );

  return { isLoading, error, sendRequest, clearError };
};
